(ns motorsaif.web.components.step-form
  (:require
   ["react-transition-group" :refer [CSSTransition SwitchTransition]]
   [motorsaif.i18n :as i18n :refer [tr]]
   [motorsaif.shared.step-form :as ssf]
   [motorsaif.string :as mstr :refer [format]]
   [re-frame.core :as rf]
   [reagent.core :as r]
   [taoensso.encore :as encore]))



(def ^:private next-fn-limiter (encore/rate-limiter {:default [1 (encore/ms :secs 0.3)]}))

(def ^:private kw->pred
  {:= =
   :not= not=})

(defn- next-condition [{:keys [form branch] :as argz}]
  (cond (keyword? branch)
        branch
        (map? branch)
        (let [[next-node condition] (first branch)
              [pred path test-val] condition
              pred (or (kw->pred pred) pred)
              {:keys [get-val]} form
              curr-val (get-val path)]
          (when (pred curr-val test-val) next-node))))

(defn- -next-fn [{:keys [*path form steps] :as argz}]
  (let [current-node (get steps (last @*path))
        next (:next current-node)
        ret (cond (vector? next)
                  (some #(next-condition {:branch %
                                          :form form}) next)
                  (keyword? next)
                  next)]
    ret))

(defn validated-inputs [{:keys [form validate]}]
  (let [{:keys [*realtime-validation]} form
        realtime-validation (:human @*realtime-validation)]
    (map #(hash-map :path %
                    :valid (get-in realtime-validation %)) validate)))

(defn step-form [{:keys [*path init-path steps form title no-progress steps-limit
                         prev-fn
                         *save-disabled]
                  :or {steps-limit 99}
                  }]
  (let [trs (i18n/tr-scope [:step-form])
        *path (or *path (r/atom init-path))
        next-fn (fn -inext-fn
                  ([] (-inext-fn nil))
                  ([next-node-k]
                   (let [[_ backoff] (next-fn-limiter true)
                         next-node (or (and (keyword? next-node-k)
                                            next-node-k)
                                       (-next-fn {:form form
                                                  :steps steps
                                                  :*path *path}))]
                     (when-not backoff
                       (swap! *path conj next-node)))))

        prev-fn (or prev-fn (fn []
                              (swap! *path pop)))]
    (fn []
      (let [k (last @*path)
            current (get steps k)
            _ (assert current (format "No node found for key: %s" k))
            finish? (:finish current)
            validate (:validate current)
            ;; percent-complete (* 100 (/ (dec (count @*path)) (- (count steps) 2)))
            percent-complete (* 100 (/ (dec (count @*path)) (dec (count steps))))
            validated (validated-inputs {:form form
                                         :validate validate})
            all-valid? (every? nil? (map :valid validated))]

        [:div#step-form
         [:div#header
          (when (> (count @*path) 1)
            [:button.btn.ghost
             {:on-click prev-fn
              :type "button"}
             [:i.fas.fa-arrow-left]])
          [:div.center
           ^{:key "prog"}
           (when title
             [:div.title title])
           (when-not no-progress
             [:div.progress
              [:div.progress-inner
               {:style {:width (format "%s%" percent-complete)}}]])]]

         [:> SwitchTransition
          [:> CSSTransition
           {:timeout 100
            :key (name k)
            :class-names "step-form"}
           [:div#content
            {:class (name k)}
            [(:renderer current) {:form form
                                  :next-fn next-fn
                                  :prev-fn prev-fn}]]]]
         (when-not (>= (count @*path) steps-limit)
           [:div#bottom
            (condp = finish?
              :update
              [:button.btn.primary.full.square
               {:disabled (or (and *save-disabled @*save-disabled) (not all-valid?))
                :type "submit"}
               (trs :save "Save Changes")]
              true
              [:button.btn.primary.full.square
               {:disabled (not all-valid?)
                :type "submit"}
               (trs :submit "Submit")]
              [:button.btn.primary.full.square
               {:on-click next-fn
                :disabled (not all-valid?)
                :type "button"}
               (trs :continue "Continue")])])]))))
;; In your UI namespace, e.g., your.namespace.ui.cljs

(defn step-form-ui
  "Renders the UI for a multi-step form based on state managed externally.

  Arguments:
    A map containing:
    :steps        - Map. Step configurations (passed to state manager).
    :form         - Atom. Form data (passed to state manager).
    :init-path    - Vector. Initial step path (passed to state manager).
    :*path        - Optional. Atom. Existing path atom (passed to state manager).
    :prev-fn      - Optional. Function. Custom previous step logic (passed to state manager).
    :steps-limit  - Optional. Number. Limit for steps (passed to state manager).
    :title        - Optional. String. Title to display above the progress bar.
    :no-progress  - Optional. Boolean. If true, hides the progress bar.
    :*save-disabled - Optional. Atom<boolean>. External condition to disable save/submit."
  [{:keys [steps form init-path *path prev-fn steps-limit ; State manager args
           title no-progress *save-disabled] ; UI-specific args

    :or {steps-limit 99}
    :as   all-args}]
  ;; 1. Create the step form state using the dedicated function
  ;;    Pass only the arguments needed by the state management function.
  (let [state-args (select-keys all-args [:*path :init-path :steps :form :steps-limit :prev-fn])
        {:keys [*path ; Get the potentially created/passed path atom back
                current ; Reaction: current step config
                next-fn ; Function: go to next step
                prev-fn ; Function: go to previous step
                finish? ; Reaction: is current step the last?
                percent-complete ; Reaction: progress percentage
                all-valid? ; Reaction: are current inputs valid?
                ]} (ssf/make-step-form state-args)
        trs (i18n/tr-scope [:step-form])]

    ;; 2. Return the rendering function (Form-2 component)
    (fn []
      ;; Dereference reactions to get current values for rendering
      (let [current-step @current
            current-key (last @*path) ; Need the key for transitions/styling
            is-finish @finish?
            is-all-valid @all-valid?
            progress @percent-complete]

        [:div#step-form
         [:div#header
          ;; Use the prev-fn and *path from the state manager
          (when (> (count @*path) 1)
            [:button.btn.ghost
             {:on-click prev-fn
              :type "button"}
             [:i.fas.fa-arrow-left]])
          [:div.center
           ^{:key "prog"}
           (when title
             [:div.title title])
           (when-not no-progress
             [:div.progress
              [:div.progress-inner
               ;; Use the percent-complete reaction value
               {:style {:width (format "%s%" progress)}}]])]]

         ;; Use the current-key for transitions
         [:> SwitchTransition
          [:> CSSTransition
           {:timeout 100
            :key (name current-key)
            :class-names "step-form"}
           [:div#content
            {:class (name current-key)}
            ;; Render the component defined in the current step's config
            ;; Pass form atom and navigation functions from state manager
            [(:renderer current-step) {:form form
                                       :next-fn next-fn
                                       :prev-fn prev-fn}]]]]

         ;; Use steps-limit and *path from state manager
         (when-not (>= (count @*path) steps-limit)
           [:div#bottom
            (condp = is-finish ; Use dereferenced finish? reaction
              :update
              [:button.btn.primary.full.square
               ;; Use all-valid? reaction and external *save-disabled
               {:disabled (or (and *save-disabled @*save-disabled) (not is-all-valid))
                :type "submit"}
               (trs :save "Save Changes")]
              true
              [:button.btn.primary.full.square
               {:disabled (not is-all-valid) ; Use all-valid? reaction
                :type "submit"}
               (trs :submit "Submit")]
              ;; Default case (not finish)
              [:button.btn.primary.full.square
               {:on-click next-fn ; Use next-fn from state manager
                :disabled (not is-all-valid) ; Use all-valid? reaction
                :type "button"}
               (trs :continue "Continue")])])]))))
